'use client';

/**
 * Architecture Capability Map — composition card.
 *
 * Copy of components/widgets/arch-canvas-widget.tsx, adapted to live in
 * the card registry (content_key `arch_canvas_canvas`) — self-sufficient
 * height so it survives a bare Canvas2Card mount. The original stays put
 * for the legacy `docs` mode until that mode is retired; this is the one
 * the cards substrate uses (cp-and-reuse, not modify-in-place).
 *
 * @component_role Architecture Capability Map Visualization
 * @business_rule Displays the system architecture as an interactive reagraph
 *   showing packages, capabilities, features, and optionally user stories.
 * @user_outcome Explore the Klyde platform architecture visually.
 *
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain docs
 */

import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { api } from '@/lib/api/client';
import {
  Loader2,
  Network,
  ZoomIn,
  ZoomOut,
  Maximize2,
  MousePointer2,
  Package,
  Target,
  Layers,
  BookOpen,
} from 'lucide-react';
import { useTheme } from 'next-themes';
import { useGraphSelectionStore } from '@/lib/stores/graph-selection-store';

interface ArchNode {
  id: string;
  label: string;
  type: string; // system, package, capability, feature, story
  description?: string;
  extra?: { story_count?: number };
}

interface ArchEdge {
  id: string;
  source: string;
  target: string;
  type: string; // HAS_PACKAGE, PROVIDES, HAS_FEATURE, HAS_STORY
}

interface ArchCanvasResponse {
  nodes: ArchNode[];
  edges: ArchEdge[];
  metadata: {
    node_count: number;
    edge_count: number;
    packages: number;
    capabilities: number;
    features: number;
    stories: number;
  };
}

interface ArchCapabilityMapCardProps {
  includeStories?: boolean;
  focusType?: string;
  focusId?: string;
}

// Node colors by type - architecture hierarchy
const NODE_COLORS: Record<string, string> = {
  system: '#F5A623',      // Orange for system root
  package: '#BD10E0',     // Purple for packages
  capability: '#4DD0E1',  // Cyan for capabilities
  feature: '#50E3C2',     // Teal for features
  story: '#7B68EE',       // Medium slate blue for stories
};

// Node sizes by type - hierarchy levels
const NODE_SIZES: Record<string, number> = {
  system: 16,
  package: 14,
  capability: 12,
  feature: 8,
  story: 5,
};

// Edge colors by relationship type
const EDGE_COLORS: Record<string, string> = {
  HAS_PACKAGE: '#BD10E0',
  PROVIDES: '#4DD0E1',
  HAS_FEATURE: '#50E3C2',
  HAS_STORY: '#7B68EE',
  default: '#666666',
};

// Icons by type
const TYPE_ICONS: Record<string, typeof Package> = {
  system: Network,
  package: Package,
  capability: Target,
  feature: Layers,
  story: BookOpen,
};

export function ArchCapabilityMapCard({
  includeStories = true,
  focusType,
  focusId,
}: ArchCapabilityMapCardProps) {
  const [isClient, setIsClient] = useState(false);
  const [GraphCanvas, setGraphCanvas] = useState<any>(null);
  const [themes, setThemes] = useState<{ dark: any; light: any } | null>(null);
  const { resolvedTheme } = useTheme();
  const selectNode = useGraphSelectionStore((state) => state.selectNode);
  const graphRef = useRef<any>(null);

  // Data state
  const [data, setData] = useState<ArchCanvasResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Selection and interaction state
  const [selectedNodes, setSelectedNodes] = useState<string[]>([]);
  const [activeNodes, setActiveNodes] = useState<string[]>([]);

  // Category filter — node types toggled off via the legend. Empty = all shown.
  const [hiddenTypes, setHiddenTypes] = useState<Set<string>>(() => new Set());
  const toggleType = useCallback((type: string) => {
    setHiddenTypes((prev) => {
      const next = new Set(prev);
      if (next.has(type)) next.delete(type);
      else next.add(type);
      return next;
    });
  }, []);

  // Load reagraph dynamically (client-side only)
  useEffect(() => {
    setIsClient(true);
    import('reagraph').then((mod) => {
      setGraphCanvas(() => mod.GraphCanvas);
      setThemes({ dark: mod.darkTheme, light: mod.lightTheme });
    });
  }, []);

  // Fetch architecture data
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams();
        if (includeStories) params.set('include_stories', 'true');

        const result = await api.get<ArchCanvasResponse>(
          `/docs/arch/canvas${params.toString() ? '?' + params.toString() : ''}`
        );
        setData(result);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load architecture');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [includeStories]);

  // Transform to reagraph format — category filter drops hidden types here,
  // and edges to/from a hidden node go with them.
  const visibleNodes = useMemo(
    () => (data ? data.nodes.filter((n) => !hiddenTypes.has(n.type)) : []),
    [data, hiddenTypes],
  );
  const visibleIds = useMemo(() => new Set(visibleNodes.map((n) => n.id)), [visibleNodes]);

  const graphNodes = useMemo(
    () =>
      visibleNodes.map((node) => ({
        id: node.id,
        label: node.label,
        fill: NODE_COLORS[node.type] || NODE_COLORS.feature,
        size: NODE_SIZES[node.type] || 8,
        cluster: node.type, // Group by type for force-directed layout
        data: {
          type: node.type,
          description: node.description,
          storyCount: node.extra?.story_count,
        },
      })),
    [visibleNodes],
  );

  const graphEdges = useMemo(() => {
    if (!data) return [];
    return data.edges
      .filter((edge) => visibleIds.has(edge.source) && visibleIds.has(edge.target))
      .map((edge) => ({
        id: edge.id,
        source: edge.source,
        target: edge.target,
        label: edge.type.replace(/_/g, ' '),
        fill: EDGE_COLORS[edge.type] || EDGE_COLORS.default,
      }));
  }, [data, visibleIds]);

  // Handle node click
  const handleNodeClick = useCallback(
    (node: any) => {
      const archNode = data?.nodes.find((n) => n.id === node.id);
      if (archNode) {
        selectNode({
          id: archNode.id,
          label: archNode.label,
          type: archNode.type,
        });
        setSelectedNodes([node.id]);
      }
    },
    [data, selectNode]
  );

  // Zoom controls
  const handleZoomIn = useCallback(() => {
    graphRef.current?.zoomIn?.();
  }, []);

  const handleZoomOut = useCallback(() => {
    graphRef.current?.zoomOut?.();
  }, []);

  const handleFitView = useCallback(() => {
    graphRef.current?.fitNodesInView?.();
  }, []);

  const handleCenterGraph = useCallback(() => {
    graphRef.current?.centerGraph?.();
  }, []);

  // Current theme
  const currentTheme = useMemo(() => {
    if (!themes) return null;
    return resolvedTheme === 'dark' ? themes.dark : themes.light;
  }, [themes, resolvedTheme]);

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        <span className="ml-2 text-muted-foreground">Loading architecture...</span>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex items-center justify-center h-full text-destructive">
        <Network className="w-8 h-8 mr-2" />
        <span>{error}</span>
      </div>
    );
  }

  // No data
  if (!data || data.nodes.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
        <Network className="w-16 h-16 mb-4 opacity-50" />
        <p className="text-lg">No architecture data</p>
        <p className="text-sm">Run sync to populate the graph</p>
      </div>
    );
  }

  // Client-side only render
  if (!isClient || !GraphCanvas || !currentTheme) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    // onWheelCapture stops the wheel from bubbling to the Canvas2Card scroll
    // container, so scroll-over-graph drives the camera instead of the page.
    <div
      className="relative h-full w-full min-h-[480px]"
      onWheelCapture={(e) => e.stopPropagation()}
    >
      {/* Toolbar */}
      <div className="absolute top-2 right-2 z-10 flex gap-1 bg-background/80 backdrop-blur-sm rounded-md border p-1">
        <button
          onClick={handleZoomIn}
          className="p-1.5 hover:bg-accent rounded"
          title="Zoom In"
        >
          <ZoomIn className="w-4 h-4" />
        </button>
        <button
          onClick={handleZoomOut}
          className="p-1.5 hover:bg-accent rounded"
          title="Zoom Out"
        >
          <ZoomOut className="w-4 h-4" />
        </button>
        <button
          onClick={handleFitView}
          className="p-1.5 hover:bg-accent rounded"
          title="Fit to View"
        >
          <Maximize2 className="w-4 h-4" />
        </button>
        <button
          onClick={handleCenterGraph}
          className="p-1.5 hover:bg-accent rounded"
          title="Center Graph"
        >
          <MousePointer2 className="w-4 h-4" />
        </button>
      </div>

      {/* Legend = category filter — click a row to hide/show that node type. */}
      <div className="absolute bottom-2 left-2 z-10 flex flex-col gap-0.5 bg-background/80 backdrop-blur-sm rounded-md border p-2 text-xs">
        {Object.entries(NODE_COLORS).map(([type, color]) => {
          const Icon = TYPE_ICONS[type] || Layers;
          const hidden = hiddenTypes.has(type);
          return (
            <button
              key={type}
              type="button"
              onClick={() => toggleType(type)}
              title={hidden ? `Show ${type}` : `Hide ${type}`}
              className={`flex items-center gap-2 rounded px-1 py-0.5 hover:bg-accent transition-opacity ${hidden ? 'opacity-35' : ''}`}
            >
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: color }} />
              <Icon className="w-3 h-3" style={{ color }} />
              <span className={`capitalize ${hidden ? 'line-through' : ''}`}>{type}</span>
            </button>
          );
        })}
      </div>

      {/* Stats */}
      <div className="absolute top-2 left-2 z-10 bg-background/80 backdrop-blur-sm rounded-md border px-2 py-1 text-xs">
        <span className="text-muted-foreground">
          {data.metadata.packages} packages · {data.metadata.capabilities} capabilities · {data.metadata.features} features
          {data.metadata.stories > 0 && ` · ${data.metadata.stories} stories`}
        </span>
      </div>

      {/* Graph */}
      <GraphCanvas
        ref={graphRef}
        nodes={graphNodes}
        edges={graphEdges}
        theme={currentTheme}
        layoutType="forceDirected2d"
        clusterAttribute="cluster"
        labelType="nodes"
        draggable
        animated
        cameraMode="pan"
        edgeArrowPosition="end"
        edgeLabelPosition="above"
        selections={selectedNodes}
        actives={activeNodes}
        onNodeClick={handleNodeClick}
        onNodePointerOver={(node: any) => setActiveNodes([node.id])}
        onNodePointerOut={() => setActiveNodes([])}
      />
    </div>
  );
}
